import { useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { CRYPTO_REDEMPTION_SCREEN_KEYS } from 'models/Redemptions/constants'

import TextInput from 'components/TextInput'

import PrimaryLink from 'units/PrimaryLink'
import { STRING, EVENT_TYPE } from 'resources'
import { sendAnalyticsEvent } from 'services/analytics'
import { validateWalletAddress } from 'utils/validators'
import { getPolygonLearnMoreUrl } from 'models/General/selectors'

import {
  Form,
  PolygonWarningContainer,
  ContinueButton,
  ExclamationContainer,
  ExclamationDescription,
  ButtonContainer,
  PasteAddressButton,
  InfoIconRed,
} from '../styles'

const WalletAddressEnteringScreen = ({ goToNextScreen, crypto, walletAddress, setWalletAddress }) => {
  const polygonLearnMoreUrl = useSelector(getPolygonLearnMoreUrl)
  const isWalletAddressValid = useMemo(() => {
    const cryptoTickerName = STRING.crypto[crypto].ticker
    return !walletAddress || validateWalletAddress(walletAddress, cryptoTickerName)
  }, [walletAddress, crypto])

  useEffect(() => {
    sendAnalyticsEvent(EVENT_TYPE.redeemCryptoWalletScreenView, { crypto })
  }, [crypto])

  const handleContinue = () => {
    if (!walletAddress || !isWalletAddressValid) {
      return
    }
    sendAnalyticsEvent(EVENT_TYPE.redeemCryptoWalletScreenContinueClick, { crypto })
    goToNextScreen(CRYPTO_REDEMPTION_SCREEN_KEYS.confirmationScreen)
  }

  const handleChange = (event) => {
    setWalletAddress(event?.target?.value || '')
  }

  const handlePaste = async (event) => {
    event.preventDefault()

    sendAnalyticsEvent(EVENT_TYPE.redeemCryptoWalletScreenPasteAddressClick, { crypto })
    try {
      const text = await navigator.clipboard.readText()
      setWalletAddress(text)
    } catch (err) {
      console.log('[Crypto Redemption] Failed to read clipboard contents: ', err) // eslint-disable-line no-console
    }
  }

  return (
    <>
      {crypto === 'usdc' && (
        <PolygonWarningContainer>
          {STRING.cryptoRedeemScreen.walletEnteringScreen.polygonWarning}
          {polygonLearnMoreUrl && (
            <PrimaryLink to={polygonLearnMoreUrl}>
              {STRING.cryptoRedeemScreen.walletEnteringScreen.polygonLearnMore}
            </PrimaryLink>
          )}
        </PolygonWarningContainer>
      )}
      <Form onSubmit={handleContinue}>
        <TextInput
          invalid={!isWalletAddressValid}
          type="text"
          placeholder={STRING.cryptoRedeemScreen.walletEnteringScreen.formPlaceholder}
          onChange={handleChange}
          errorMessage={
            !isWalletAddressValid && (
              <div>
                {STRING.cryptoRedeemScreen.walletEnteringScreen.walletAddressErrorMessage(STRING.crypto[crypto].ticker)}
              </div>
            )
          }
          value={walletAddress}
        />
        <PasteAddressButton type="button" variant="secondary" onClick={handlePaste} iconName="copy">
          {STRING.cryptoRedeemScreen.walletEnteringScreen.pasteAddressButtonText}
        </PasteAddressButton>
        <ExclamationContainer>
          <InfoIconRed />
          <ExclamationDescription>
            {STRING.cryptoRedeemScreen.walletEnteringScreen.incorrectAddressWarning}
          </ExclamationDescription>
        </ExclamationContainer>
        <ButtonContainer>
          <ContinueButton
            onPress={handleContinue}
            type="submit"
            disabled={!isWalletAddressValid || !walletAddress}
            withArrow
          >
            {STRING.cryptoRedeemScreen.walletEnteringScreen.continueButton}
          </ContinueButton>
        </ButtonContainer>
      </Form>
    </>
  )
}

WalletAddressEnteringScreen.propTypes = {
  crypto: PropTypes.string.isRequired,
  goToNextScreen: PropTypes.func.isRequired,
  setWalletAddress: PropTypes.func.isRequired,
  walletAddress: PropTypes.string.isRequired,
}

export default WalletAddressEnteringScreen
